import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { formatDay, formatPhone, formatShort, isIsoDate, tiffinsText, type KitchenCustomer, type Meal } from '@tiffinify/core';
import { BtnPrimary, Card, Empty, FoodMark, Helper, LoadError, PageLoader, Segmented, TaskTitle, TopBar } from '../components/ui';
import { mealCount, mealName } from '../lib/format';
import { useKitchenDay, useMe } from '../lib/queries';

/** A standard A4 sticker sheet: 3 across, 8 down, 70 × 37 mm each. */
const PER_SHEET = 24;
/** A4 at 96 dpi, for fitting the preview to the screen. */
const A4_PX = 794;
const MEALS: Meal[] = ['breakfast', 'lunch', 'dinner'];

type Mode = 'labels' | 'list';

export function Print() {
  const [params] = useSearchParams();
  const me = useMe();
  const asked = params.get('date');
  const day = asked && isIsoDate(asked) ? asked : me.data?.today;
  const askedMeal = params.get('meal') as Meal | null;
  const meal: Meal = askedMeal && MEALS.includes(askedMeal) ? askedMeal : 'lunch';
  const [mode, setMode] = useState<Mode>(params.get('show') === 'list' ? 'list' : 'labels');
  const k = useKitchenDay(day, meal);
  const d = k.data;
  const kitchenName = me.data?.kitchen.name ?? '';
  const back = `/kitchen?date=${day ?? ''}&meal=${meal}`;

  // The preview is the real A4 page, shrunk to the screen's width; printing uses it at full size.
  const fit = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  useLayoutEffect(() => {
    const el = fit.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => { if (e) setZoom(Math.min(1, e.contentRect.width / A4_PX)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [d]);
  useEffect(() => { document.title = `${mode === 'labels' ? 'Labels' : 'Delivery list'} · ${kitchenName || 'Tiffinify'}`; return () => { document.title = 'Tiffinify'; }; }, [mode, kitchenName]);

  const head = (
    <>
      <TopBar to={back} label="Back to Kitchen" />
      <TaskTitle title="Print labels and list" sub={d ? `${formatDay(d.day)} · ${mealName(d.meal)} · ${mealCount(d.meal, d.sheet.total)}` : undefined} />
    </>
  );
  if (k.isError && !d) return <main className="main task">{head}<LoadError what="this meal" onRetry={() => void k.refetch()} /></main>;
  if (!d) return <main className="main task">{head}<PageLoader label="Getting the list" /></main>;

  // Labels go in route order: area by area, as the packing list is.
  const customers = d.sheet.areas.flatMap((a) => a.customers);
  const sheets: KitchenCustomer[][] = [];
  for (let i = 0; i < customers.length; i += PER_SHEET) sheets.push(customers.slice(i, i + PER_SHEET));
  const when = `${formatDay(d.day)} · ${mealName(d.meal)}`;

  return (
    <main className="main task print-screen">
      {head}
      {customers.length === 0 ? (
        <Card><Empty title="Nothing to print">No one has {d.meal} on this day.</Empty></Card>
      ) : (
        <>
          <Segmented label="What to print" value={mode} onChange={setMode} options={[{ value: 'labels', label: 'Labels' }, { value: 'list', label: 'Delivery list' }]} />
          <Helper>
            {mode === 'labels'
              ? `${customers.length} ${customers.length === 1 ? 'label' : 'labels'} on ${sheets.length} A4 ${sheets.length === 1 ? 'sheet' : 'sheets'} of 24 (70 × 37 mm). Print at 100% scale with no margins.`
              : `${customers.length} ${customers.length === 1 ? 'stop' : 'stops'} by area, with a box to tick. Any A4 paper.`}
          </Helper>
          <BtnPrimary label={mode === 'labels' ? 'Print labels' : 'Print delivery list'} icon="check" onClick={() => window.print()} />

          <div className="print-fit" ref={fit}>
            <div className="print-area" style={{ zoom }}>
              {mode === 'labels'
                ? sheets.map((page, i) => (
                    <section key={i} className="print-label-sheet" aria-label={`Label sheet ${i + 1} of ${sheets.length}`}>
                      {page.map((c) => <Label key={c.subscriptionId} c={c} meal={d.meal} day={d.day} kitchen={kitchenName} />)}
                    </section>
                  ))
                : <DeliveryList areas={d.sheet.areas} when={when} kitchen={kitchenName} total={d.sheet.total} meal={d.meal} />}
            </div>
          </div>
        </>
      )}
    </main>
  );
}

/** One bag's sticker: whose kitchen, who it's for, what's inside, and anything the packer must not miss. */
function Label({ c, meal, day, kitchen }: { c: KitchenCustomer; meal: Meal; day: string; kitchen: string }) {
  const portions = c.tiffins.reduce((n, x) => n + x.quantity, 0);
  const foods = [...new Set(c.tiffins.map((x) => x.food))];
  return (
    <div className="print-label">
      <div className="print-label-head">
        <span className="print-label-kitchen">{kitchen}</span>
        <span>{mealName(meal)} · {formatShort(day)}</span>
      </div>
      <div className="print-label-top">
        <span className="print-label-marks">{foods.map((f) => <FoodMark key={f} food={f} />)}</span>
        <b className="print-label-name">{c.name}</b>
        {portions > 1 && <span className="print-label-count">×{portions}</span>}
      </div>
      <div className="print-label-tiffins">{tiffinsText(c.tiffins)}</div>
      {c.note && <div className="print-label-note">{c.note}</div>}
      <div className="print-label-foot">
        <span className="print-label-area">{c.area ?? ''}</span>
        {c.lastDay && <span className="print-label-last">Last day</span>}
      </div>
    </div>
  );
}

function DeliveryList({ areas, when, kitchen, total, meal }: { areas: { name: string; count: number; customers: KitchenCustomer[] }[]; when: string; kitchen: string; total: number; meal: Meal }) {
  return (
    <section className="list-sheet" aria-label="Delivery list">
      <header className="list-head">
        <b>{kitchen}</b>
        <span>{when} · {mealCount(meal, total)}</span>
      </header>
      {areas.map((a) => (
        <div key={a.name} className="list-area">
          <h3>{a.name} <small>{mealCount(meal, a.count)}</small></h3>
          <table>
            <tbody>
              {a.customers.map((c) => (
                <tr key={c.subscriptionId}>
                  <td className="list-tick"><span aria-hidden="true" /></td>
                  <td className="list-name"><span className="print-label-marks">{[...new Set(c.tiffins.map((x) => x.food))].map((f) => <FoodMark key={f} food={f} />)}</span>{c.name}{c.lastDay && <em> · last day</em>}</td>
                  <td className="list-tiffins">{tiffinsText(c.tiffins)}</td>
                  <td className="list-phone">{c.phone ? formatPhone(c.phone) : ''}</td>
                  <td className="list-note">{c.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
